<template>
  <div :class="computedClass">
    <JadeLabel
      :optional="optional"
      :error="error"
      :disabled="disabled"
      :hidden="hideLabel"
      :htmlFor="label"
    >
      {{ label }}
    </JadeLabel>
    <div :class="computedContentClass">
      <div
        v-if="leadingItem"
        class="jade-input-field__content__leading-item"
      >
        <JadeIcon
          v-if="isIconAdornment(leadingItem)"
          size="medium"
          color="current-color"
          :use="leadingItem?.content"
        />
        <JadeText
          v-if="isTextAdornment(leadingItem)"
          variant="text-medium"
          weight="regular"
          color="current-color"
        >
          {{ leadingItem?.content }}
        </JadeText>
      </div>

      <component
        :value="value"
        ref="input"
        v-bind="$attrs"
        :is="as"
        :id="id"
        :type="type"
        :disabled="disabled"
        :aria-required="!optional"
        :aria-label="label"
        :maxlength="maxlength"
        @input="
          $event => {
            $emit('update:value', $event.target.value)
            $emit('input', $event.target.value)
          }
        "
      />

      <div
        v-if="isIconAdornment(trailingItem)"
        class="jade-input-field__content__trailing-item"
      >
        <JadeIconButton
          v-if="isIconButton"
          size="medium"
          variant="neutral-ghost"
          :icon="trailingItem.content"
          @click="$emit('onTrailingItemClick')"
        />
        <JadeIcon
          v-else
          size="medium"
          color="medium"
          :use="trailingItem.content"
        />
      </div>

      <JadeText
        v-if="isTextAdornment(trailingItem)"
        variant="text-medium"
        color="medium"
      >
        {{ trailingItem.content }}
      </JadeText>
    </div>
    <JadeSupportText :error="error">
      {{ supportText }}
    </JadeSupportText>
  </div>
</template>

<script>
import JadeText from '../JadeText'
import JadeIcon from '../JadeIcon'
import JadeIconButton from '../JadeIconButton'
import JadeLabel from '../JadeLabel'
import JadeSupportText from '../JadeSupportText'

import SIZE_ENUM from '../enums/sizes.enum'

const TYPE_VALUES_ENUM = [
  'date',
  'datetime-local',
  'email',
  'number',
  'password',
  'tel',
  'text',
  'time',
  'url'
]

const ADORNMENT_TYPES_ENUM = {
  icon: 'icon',
  text: 'text'
}

export default {
  name: 'JadeInputField',
  components: {
    JadeText,
    JadeIcon,
    JadeIconButton,
    JadeLabel,
    JadeSupportText
  },
  props: {
    value: [String, Number],
    supportText: String,
    id: String,
    label: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'text',
      validation: t => TYPE_VALUES_ENUM.includes(t)
    },
    as: {
      type: String,
      default: 'input'
    },
    size: {
      type: String,
      default: SIZE_ENUM.LARGE,
      validation: size => [SIZE_ENUM.LARGE, SIZE_ENUM.MEDIUM].includes(size)
    },
    disabled: Boolean,
    optional: Boolean,
    error: Boolean,
    hideLabel: Boolean,
    maxlength: Number,
    leadingItem: {
      type: Object,
      validation: adornment =>
        Object.values(ADORNMENT_TYPES_ENUM).includes(adornment?.type)
    },
    trailingItem: {
      type: Object,
      validation: adornment =>
        Object.values(ADORNMENT_TYPES_ENUM).includes(adornment?.type)
    }
  },
  watch: {
    value (value) {
      this.$refs.input.value = value
    }
  },
  computed: {
    computedClass () {
      return {
        'jade-input-field': true,
        'is-disabled': this.disabled,
        'is-errored': this.error,
        'is-hidden': this.hidden,
        'is-trailing-text': this.isTextAdornment(this.trailingItem),
        [`is-${this.size}`]: this.size
      }
    },
    computedContentClass () {
      return {
        'jade-input-field__content': true,
        'is-errored': this.error
      }
    },
    isIconButton () {
      return Boolean(this.$listeners?.onTrailingItemClick) && !this.disabled
    }
  },
  methods: {
    isTextAdornment (adornment) {
      return adornment?.type === ADORNMENT_TYPES_ENUM.text
    },
    isIconAdornment (adornment) {
      return adornment?.type === ADORNMENT_TYPES_ENUM.icon
    }
  }
}
</script>

<style scoped>
@import '@stone-payments/jade/css/input-field.css';
</style>
